import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'en' | 'de';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  resetLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Function to detect if user is in Germany
const detectGermanLocation = (): boolean => {
  try {
    // Check browser language
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('de')) {
      return true;
    }

    // Check timezone (Germany is in UTC+1/UTC+2)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone.includes('Berlin') || timezone.includes('Europe/Berlin')) {
      return true;
    }

    // Check if user has previously selected German
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage === 'de') {
      return true;
    }

    return false;
  } catch (error) {
    // Fallback to English if detection fails
    return false;
  }
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    // Load language preference from localStorage
    const savedLanguage = localStorage.getItem('language') as Language;

    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'de')) {
      // User has explicitly set a language preference
      setLanguageState(savedLanguage);
    } else {
      // Auto-detect based on location/country
      const isGermanLocation = detectGermanLocation();
      const defaultLanguage: Language = isGermanLocation ? 'de' : 'en';
      setLanguageState(defaultLanguage);

      // Save the detected language to localStorage
      if (localStorage.getItem('cookieConsent') === 'accepted') {
        localStorage.setItem('language', defaultLanguage);
      }
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    // Only save to localStorage if it exists (user has accepted cookies)
    if (localStorage.getItem('cookieConsent') === 'accepted') {
      localStorage.setItem('language', lang);
    }
  };

  const resetLanguage = () => {
    // Reset to auto-detected language instead of hardcoded English
    const isGermanLocation = detectGermanLocation();
    const defaultLanguage: Language = isGermanLocation ? 'de' : 'en';
    setLanguageState(defaultLanguage);
    localStorage.removeItem('language');
  };

  const t = (key: string): string => {
    // Simple translation function - you can expand this as needed
    const translations: Record<string, Record<Language, string>> = {
      'language.en': { en: 'English', de: 'Englisch' },
      'language.de': { en: 'German', de: 'Deutsch' },
      'nav.home': { en: 'Home', de: 'Startseite' },
      'nav.about': { en: 'About', de: 'Über uns' },
      'nav.donate': { en: 'Donate', de: 'Spenden' },
      'nav.impressum': { en: 'Legal Notice', de: 'Impressum' },
      'nav.privacy': { en: 'Privacy Policy', de: 'Datenschutz' },
      'nav.cookies': { en: 'Cookie Policy', de: 'Cookie-Erklärung' },
    };

    const translation = translations[key];
    if (translation) {
      return translation[language] || key;
    }

    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, resetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
